import { ErrorNotice } from '@/components/ui/Feedback'
import { PageHeader, pageClassName } from '@/components/ui/Page'
import { ArrowRight, Headphones, Play, Search, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { useLibrary, usePlaylists, useRecentHistory, useSimilar } from '@/api/hooks'
import type { Track } from '@/api/types'
import { usePlayer } from '@/audio/store'
import { Cover } from '@/components/Cover'
import { PlayingIndicator } from '@/components/PlayingIndicator'
import { Button, EmptyState, Skeleton, Spinner, TrackListSkeleton } from '@/components/ui'
import { artistSummaryHref, similarHref } from '@/features/artists/artistHref'
import { PlaylistMosaic } from '@/features/playlists/PlaylistMosaic'
import { AddToPlaylistDialog, type AddToPlaylistTarget } from '@/features/search/AddToPlaylistDialog'
import { SearchResultRow } from '@/features/search/SearchResultRow'
import { cn } from '@/lib/cn'

import { discoverySongs, recommendationSeeds, songKey, uniqueSongs } from './homeFeed'
import { HomeSection } from './HomeSection'
import { DaylistCard } from '@/features/daylists/DaylistCard'
import { useHomePlayback } from './useHomePlayback'

const MORE = 'inline-flex min-h-11 shrink-0 items-center gap-1 text-sm font-medium text-text-muted hover:text-text'

function FeedError({ children, retry, busy }: { children: string; retry: () => void; busy: boolean }) {
  return (
    <ErrorNotice onRetry={retry} busy={busy}>{children}</ErrorNotice>
  )
}

function SongShortcuts({ songs, playback }: { songs: Track[]; playback: ReturnType<typeof useHomePlayback> }) {
  const currentId = usePlayer((state) => state.current()?.trackId)
  const playing = usePlayer((state) => state.isPlaying)
  return (
    <ul className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-2 lg:grid-cols-3">
      {songs.map((song) => {
        const key = songKey(song)
        const active = currentId === song.id
        const error = playback.errors[key]
        return (
          <li key={key} className="min-w-0">
            <button
              type="button"
              aria-label={`${active && playing ? 'Pause' : 'Play'} ${song.title} by ${song.artist_name}`}
              disabled={playback.busy[key]}
              onClick={() => {
                if (active) {
                  const player = usePlayer.getState()
                  if (playing) player.pause()
                  else player.play()
                } else void playback.act(song)
              }}
              className={cn('group flex w-full items-center gap-3 overflow-hidden rounded-lg border border-border bg-surface-raised p-2 text-left transition-colors hover:bg-surface-hover disabled:opacity-60', active && 'border-accent/40 bg-accent-soft')}
            >
              <span className="relative shrink-0">
                <Cover src={song.artwork_url} size="md" />
                <span className={cn('absolute inset-0 flex items-center justify-center rounded-md bg-black/40 text-white transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100', active || playback.busy[key] ? 'opacity-100' : 'opacity-0')}>
                  {playback.busy[key] ? <Spinner size="sm" label="Preparing song" /> : active
                    ? <PlayingIndicator animated={playing} />
                    : <Play className="h-5 w-5 fill-current" aria-hidden="true" />}
                </span>
              </span>
              <span className="min-w-0">
                <span className={cn('block truncate text-sm font-medium', active ? 'text-accent' : 'text-text')}>{song.title}</span>
                <span className="mt-0.5 block truncate text-xs text-text-muted">{song.artist_name}</span>
              </span>
            </button>
            {error ? <ErrorNotice busy={playback.busy[key]} onRetry={() => void playback.act(song, error.action)}>{error.message}</ErrorNotice> : null}
          </li>
        )
      })}
    </ul>
  )
}

function Recommendations({ seed, recent, playback, onPlaylist }: {
  seed: Track
  recent: Track[]
  playback: ReturnType<typeof useHomePlayback>
  onPlaylist: (target: AddToPlaylistTarget) => void
}) {
  const similar = useSimilar({ localArtistId: seed.local_artist_id ?? undefined, artistId: seed.artist_id })
  const songs = discoverySongs(similar.data?.tracks ?? [], recent)
  const artists = (similar.data?.artists ?? []).slice(0, 6)
  return (
    <div className="space-y-8">
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-sm text-text-muted">More like <span className="font-medium text-text">{seed.artist_name}</span></p>
          <Link to={`${similarHref(seed)}?name=${encodeURIComponent(seed.artist_name)}`} className={MORE}>Explore <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
        {similar.isPending ? <TrackListSkeleton count={6} /> : similar.isError ? (
          <FeedError retry={() => void similar.refetch()} busy={similar.isFetching}>Suggestions are unavailable right now.</FeedError>
        ) : songs.length === 0 ? (
          <p className="rounded-card border border-border p-5 text-sm text-text-muted">No more suggestions yet. Import more music or try another artist.</p>
        ) : (
          <ul className="rounded-card border border-border bg-surface-raised p-1 sm:p-2">
            {songs.map((song) => {
              const key = songKey(song)
              const error = playback.errors[key]
              return <SearchResultRow
                key={key}
                result={song}
                providerName={song.provider}
                status={song.library_status ?? undefined}
                busy={playback.busy[key]}
                error={error ? { message: error.message, retry: () => void playback.act(song, error.action) } : undefined}
                onAction={(action) => void playback.act(song, action)}
                onDismissError={() => playback.dismiss(key)}
                onAddToPlaylist={() => onPlaylist({ title: song.title, provider: song.provider, externalId: song.external_id, trackId: song.track_id })}
              />
            })}
          </ul>
        )}
      </div>
      {artists.length > 0 ? (
        <HomeSection title="Artists to explore">
          <ul className="grid grid-cols-3 gap-4 sm:grid-cols-6">
            {artists.map((artist) => (
              <li key={`${artist.provider}:${artist.external_id}`} className="min-w-0">
                <Link to={artistSummaryHref(artist)} className="group block rounded-lg text-center">
                  <Cover src={artist.picture_url} size="xl" className="mx-auto max-w-28 rounded-full transition-opacity group-hover:opacity-80" />
                  <span className="mt-2 block truncate text-sm font-medium text-text">{artist.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </HomeSection>
      ) : null}
    </div>
  )
}

export function HomePage() {
  const navigate = useNavigate()
  const history = useRecentHistory(50)
  const library = useLibrary({ sort: 'recent' })
  const playlists = usePlaylists()
  const playback = useHomePlayback()
  const [selectedArtist, setSelectedArtist] = useState<number | null>(null)
  const [playlistTarget, setPlaylistTarget] = useState<AddToPlaylistTarget | null>(null)

  const recent = uniqueSongs((history.data?.results ?? []).map((event) => event.track), 50)
  const saved = library.data?.pages.flatMap((page) => page.results.map((item) => item.track)) ?? []
  const shortcuts = recent.length ? recent.slice(0, 6) : uniqueSongs(saved, 6)
  const seeds = recommendationSeeds(recent, saved)
  const seed = seeds.find((song) => song.artist_id === selectedArtist) ?? seeds[0]
  const seedFromHistory = seed && recent.some((song) => song.artist_id === seed.artist_id)
  const loading = history.isPending || library.isPending
  const collections = [...(playlists.data ?? [])].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 4)

  return (
    <div className={cn(pageClassName(), 'space-y-8 md:space-y-10')}>
      <PageHeader title="Home" subtitle="A familiar favourite. Something new." />

      <DaylistCard />

      {history.isError ? <FeedError retry={() => void history.refetch()} busy={history.isFetching}>Recent plays could not be loaded.</FeedError> : null}
      {library.isError ? <FeedError retry={() => void library.refetch()} busy={library.isFetching}>Your library could not be loaded.</FeedError> : null}

      {loading ? (
        <div role="status" aria-label="Loading your music" className="grid grid-cols-2 gap-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => <Skeleton key={index} className="h-[66px] rounded-lg" />)}
        </div>
      ) : shortcuts.length > 0 ? (
        <HomeSection title={recent.length ? 'Recently played' : 'From your library'} action={
          <Link to={recent.length ? '/settings/history' : '/library'} state={recent.length ? { fromHome: true } : undefined} className={MORE}>
            {recent.length ? 'History' : 'View all'} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        }>
          <SongShortcuts songs={shortcuts} playback={playback} />
        </HomeSection>
      ) : !history.isError && !library.isError ? (
        <div className="rounded-card border border-accent/20 bg-accent-soft">
          <EmptyState icon={Headphones} title="Find your next favourite" description="Import music you already own. No provider account is needed." action={{ label: 'Open library', icon: Search, onClick: () => void navigate('/library') }} />
        </div>
      ) : null}

      {seed ? (
        <HomeSection title="For you" subtitle={seedFromHistory ? 'Inspired by your listening.' : 'Inspired by your library.'} action={<Sparkles className="h-5 w-5 text-accent" aria-hidden="true" />}>
          {seeds.length > 1 ? (
            <div role="group" aria-label="Recommendation artists" className="mb-3 flex flex-wrap gap-2">
              {seeds.map((song) => <Button key={song.artist_id} variant={song.artist_id === seed.artist_id ? 'primary' : 'secondary'} size="sm" aria-pressed={song.artist_id === seed.artist_id} onClick={() => setSelectedArtist(song.artist_id)}>{song.artist_name}</Button>)}
            </div>
          ) : null}
          <Recommendations key={seed.artist_id} seed={seed} recent={recent} playback={playback} onPlaylist={setPlaylistTarget} />
        </HomeSection>
      ) : !loading && shortcuts.length > 0 ? (
        <HomeSection title="Find something new">
          <Link to="/search" className="flex items-center justify-between gap-4 rounded-card border border-border bg-surface-raised p-5 hover:bg-surface-hover">
            <span><span className="block font-medium text-text">Explore more music</span><span className="mt-1 block text-sm text-text-muted">Import music to build suggestions from your own library.</span></span>
            <ArrowRight className="h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
          </Link>
        </HomeSection>
      ) : null}

      {playlists.isError ? <FeedError retry={() => void playlists.refetch()} busy={playlists.isFetching}>Your playlists could not be loaded.</FeedError> : collections.length > 0 ? (
        <HomeSection title="Your playlists" action={<Link to="/library?view=playlists" className={MORE}>View all <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>}>
          <ul className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {collections.map((playlist) => <li key={playlist.id} className="min-w-0">
              <Link to={`/playlists/${playlist.id}`} className="group block rounded-card">
                <PlaylistMosaic coverUrls={playlist.cover_urls} className="w-full transition-opacity group-hover:opacity-80" />
                <span className="mt-2 block truncate text-sm font-medium text-text">{playlist.name}</span>
                <span className="mt-0.5 block text-xs text-text-muted">{playlist.track_count} {playlist.track_count === 1 ? 'song' : 'songs'}</span>
              </Link>
            </li>)}
          </ul>
        </HomeSection>
      ) : null}

      {playlistTarget ? <AddToPlaylistDialog key={`${playlistTarget.provider}:${playlistTarget.externalId}`} target={playlistTarget} onOpenChange={(open) => { if (!open) setPlaylistTarget(null) }} /> : null}
    </div>
  )
}
